import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { 
  Video, ArrowLeft, Plus, Trash2, Loader2, Clock, Lock, Unlock, Upload
} from 'lucide-react';
import { FadeIn, AnimatedCard } from '../components/ui/AnimatedCard';
import toast from 'react-hot-toast';

export function AdminVideos() {
  const queryClient = useQueryClient();
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    titleBn: '',
    description: '',
    classId: 9,
    subjectId: 25,
    isPremium: false,
  });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);

  const { data: videos, isLoading } = useQuery({
    queryKey: ['admin-videos'],
    queryFn: async () => {
      const res = await api.get('/api/videos');
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/api/videos/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-videos'] });
      toast.success('Video deleted');
    },
    onError: () => toast.error('Failed to delete video'),
  });

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoFile) {
      toast.error('Please select a video file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('title', uploadData.title);
    formData.append('titleBn', uploadData.titleBn);
    formData.append('description', uploadData.description);
    formData.append('classId', uploadData.classId.toString());
    formData.append('subjectId', uploadData.subjectId.toString());
    formData.append('isPremium', uploadData.isPremium.toString());
    formData.append('video', videoFile);
    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile);
    }

    try {
      await api.post('/api/videos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Video uploaded successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-videos'] });
      setShowUploadForm(false);
      setUploadData({ title: '', titleBn: '', description: '', classId: 9, subjectId: 25, isPremium: false });
      setVideoFile(null);
      setThumbnailFile(null);
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const allVideos = videos || [];

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      <FadeIn>
        <div className="flex items-center gap-4">
          <Link to="/admin" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Video Management</h1>
            <p className="text-slate-500">Manage video lessons</p>
          </div>
        </div>
      </FadeIn>

      {!showUploadForm ? (
        <>
          <FadeIn>
            <button
              onClick={() => setShowUploadForm(true)}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
            >
              <Upload className="w-5 h-5" />
              Upload New Video
            </button>
          </FadeIn>

          <FadeIn>
            <AnimatedCard className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-900">All Videos ({allVideos.length})</h3>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : (
                <div className="space-y-3">
                  {allVideos.map((video: any) => (
                    <div key={video.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      {video.thumbnailUrl ? (
                        <img 
                          src={video.thumbnailUrl} 
                          alt={video.title}
                          className="w-24 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                      ) : (
                        <div className="w-24 h-16 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Video className="w-6 h-6 text-slate-400" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900">{video.titleBn || video.title}</div>
                        <div className="text-sm text-slate-500 line-clamp-1">{video.description}</div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDuration(video.durationSeconds)}
                          </span>
                          <span className="flex items-center gap-1">
                            {video.isPremium ? (
                              <><Lock className="w-3 h-3" /> Premium</>
                            ) : (
                              <><Unlock className="w-3 h-3" /> Free</>
                            )}
                          </span>
                          <span>{video.views || 0} views</span>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          if (confirm('Are you sure you want to delete this video?')) {
                            deleteMutation.mutate(video.id);
                          }
                        }}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  ))}
                  {allVideos.length === 0 && (
                    <div className="text-center py-12 text-slate-400">
                      <Video className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No videos found</p>
                    </div>
                  )}
                </div>
              )}
            </AnimatedCard>
          </FadeIn>
        </>
      ) : (
        <FadeIn>
          <AnimatedCard className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Upload New Video</h2>
              <button
                onClick={() => setShowUploadForm(false)}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </div>

            <form onSubmit={handleUpload} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title (English) *</label>
                  <input
                    type="text"
                    value={uploadData.title}
                    onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title (Bengali)</label>
                  <input
                    type="text"
                    value={uploadData.titleBn}
                    onChange={(e) => setUploadData({ ...uploadData, titleBn: e.target.value })}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  rows={3}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Class ID</label>
                  <input
                    type="number"
                    value={uploadData.classId}
                    onChange={(e) => setUploadData({ ...uploadData, classId: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subject ID</label>
                  <input
                    type="number"
                    value={uploadData.subjectId}
                    onChange={(e) => setUploadData({ ...uploadData, subjectId: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPremium"
                  checked={uploadData.isPremium}
                  onChange={(e) => setUploadData({ ...uploadData, isPremium: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded border-slate-300"
                />
                <label htmlFor="isPremium" className="text-sm text-slate-700">Premium Video</label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Video File *</label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  required
                />
                {videoFile && (
                  <p className="text-sm text-slate-500 mt-1">Selected: {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Thumbnail (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? 'Uploading...' : 'Upload Video'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowUploadForm(false)}
                  className="px-6 py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </AnimatedCard>
        </FadeIn>
      )}
    </div>
  );
}
